<template>
  <div class="session-wrapper">
    <slot v-if="show.show"> </slot>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, nextTick } from "vue";

const latestProgress = ref<number | null>(null);
const show = ref({ show: true });
const resizeTimeout = ref<any>(null);
const lastWidthInPixels = ref(0);

function isMobileChrome(): boolean {
  const ua = navigator.userAgent;
  const isChrome = /Chrome/i.test(ua);
  const isMobile =
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS/i.test(ua);
  return isChrome && isMobile;
}

function onProgress(newProgress: number | null): void {
  latestProgress.value = newProgress;
}

function handleResize() {
  // Reset the session, maintaining the latest progress (if there was any)
  nextTick(() => {
    show.value.show = false;
    setTimeout(() => {
      nextTick(() => {
        show.value.show = true;
      });
    }, 0);
  });
}

function onResize() {
  // Only reset when the width changes, to prevent browser bar changes triggering a reset
  if (window.innerWidth !== lastWidthInPixels.value) {
    lastWidthInPixels.value = window.innerWidth;
    show.value.show = false;

    if (resizeTimeout.value) {
      clearTimeout(resizeTimeout.value);
    }
    resizeTimeout.value = setTimeout(() => {
      handleResize();
    }, 400);
  }
}

onMounted(() => {
  window.addEventListener("resize", onResize);
  lastWidthInPixels.value = window.innerWidth;

  if (isMobileChrome()) {
    const viewport = document.querySelector("meta[name=viewport]");
    viewport!.setAttribute(
      "content",
      `height=${document.documentElement.clientHeight}, width=device-width, initial-scale=1.0`,
    );
  }
});

onUnmounted(() => {
  window.removeEventListener("resize", onResize);

  // Reset viewport on destruction;
  if (isMobileChrome()) {
    const viewport = document.querySelector("meta[name=viewport]");
    viewport!.setAttribute("content", "width=device-width, initial-scale=1.0");
  }
});

defineExpose({
  onProgress,
  latestProgress,
});
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="less">
@import "../../styles/sessions/session.less";

// IE11 hacks to solve issues with opacity and jumpiness
@media all and (-ms-high-contrast: none) {
  .body-content > .vue-component[class^="session"] {
    // Fix opacity of overlay visuals (like the 'dark-background' on session 1)
    position: relative;
  }

  .session .background-visuals {
    // Fix jittering and broken opacity of fixed visuals (like shutters of session 1)
    position: fixed !important;
  }
}
</style>
